import { z } from "zod";

/**
 * Zod schemas allineati a `packages/dpanel-schemas/src/dpanel_schemas/users/__init__.py`
 * (Pydantic v2, Phase 5).
 *
 * Convenzione: tutti i payload di INPUT (Create, Update) hanno
 * `model_config.extra = "forbid"` lato backend; replico passandoli
 * via `.strict()` in Zod (rejecta proprietà extra). Le response
 * (Read) usano `.passthrough()` per tolleranza ai nuovi campi futuri.
 */

export const ROLES = [
  "superuser",
  "admin",
  "operator",
  "admin_read_only",
  "reseller",
  "customer",
  "peer_read",
  "member",
] as const;
export const STATUSES = ["active", "suspended", "pending_activation"] as const;

export type UserRole = (typeof ROLES)[number];
export type UserStatus = (typeof STATUSES)[number];

/** Hierarchy rank for client-side role comparisons. */
export const ROLE_LEVEL: Record<UserRole, number> = {
  // ADR-0105 B2: 'member' is the tenant sentinel (authz from membership) — lowest rank.
  member: 0,
  peer_read: 0,
  customer: 1,
  reseller: 2,
  // admin_read_only shares admin's READ rank; write controls are gated
  // additionally by isReadOnlyRole() (and the BE blocks any write regardless).
  admin_read_only: 3,
  // operator (ADR-0103): admin-tier capability parity. The no-escalation
  // carve-out is enforced in canChangeRoleTo (mirror of BE HierarchyService).
  operator: 3,
  admin: 3,
  superuser: 4,
};

/**
 * Roles an `operator` may never grant (no privilege escalation, ADR-0103 /
 * NIST AC-6) — mirror of the BE `_OPERATOR_FORBIDDEN_GRANTS`.
 */
const OPERATOR_FORBIDDEN_GRANTS: ReadonlySet<UserRole> = new Set([
  "operator",
  "admin",
  "admin_read_only",
  "superuser",
]);

/**
 * admin_read_only has full admin visibility but cannot write anything (BE
 * choke-point). Use this alongside the per-resource level check so the UI hides
 * write controls for it (it passes the level threshold but must not write).
 */
export function isReadOnlyRole(role: UserRole): boolean {
  return role === "admin_read_only";
}

/**
 * Client mirror of the BE `HierarchyService.can_role_change_to` gradient
 * (apps/api/.../users/service.py). Reseller+ only; strictly above the target's
 * current level AND not above the caller's own level; superuser is unrestricted.
 * Authoritative check stays on the BE — this only shapes the UI (which roles to
 * offer / whether to show the action).
 */
export function canChangeRoleTo(callerRole: UserRole, currentRole: UserRole, newRole: UserRole): boolean {
  const caller = ROLE_LEVEL[callerRole];
  if (caller < ROLE_LEVEL.reseller) return false;
  if (callerRole === "superuser") return true;
  // ADR-0103 no-escalation: an operator may not promote anyone into the admin tier.
  if (callerRole === "operator" && OPERATOR_FORBIDDEN_GRANTS.has(newRole)) return false;
  return caller > ROLE_LEVEL[currentRole] && caller >= ROLE_LEVEL[newRole];
}

/** Roles a caller may assign to a target (excludes the current role). */
export function assignableRoles(callerRole: UserRole, currentRole: UserRole): UserRole[] {
  return ROLES.filter((r) => r !== currentRole && canChangeRoleTo(callerRole, currentRole, r));
}

/* ────────────────────────────────────────────── Role change (POST /users/{id}/role) */

export const UserRoleChangeSchema = z
  .object({
    role: z.enum(ROLES),
    reason: z.string().min(8, "validation.role_change_reason_min").max(512, "validation.role_change_reason_max"),
  })
  .strict();

export type UserRoleChangeInput = z.infer<typeof UserRoleChangeSchema>;

/* ────────────────────────────────────────────── UserCreate (POST) */

export const UserCreateSchema = z
  .object({
    email: z.string().email("validation.email"),
    display_name: z
      .string()
      .min(2, "validation.display_name_min")
      .max(128, "validation.display_name_max"),
    role: z.enum(ROLES),
    owner_id: z.guid().optional(),
    /** Password mode: caller imposta solo `password` OR `send_activation_email`. */
    password: z
      .string()
      .min(12, "validation.password_min")
      .max(128, "validation.password_max")
      .optional(),
    send_activation_email: z.boolean().optional(),
    require_mfa: z.boolean().default(true),
    preferred_language: z.enum(["it", "en"]).default("it"),
  })
  .strict()
  .refine(
    (data) =>
      (data.password && !data.send_activation_email) ||
      (!data.password && data.send_activation_email),
    {
      message: "validation.password_xor_activation",
      path: ["password"],
    },
  );

export type UserCreateInput = z.infer<typeof UserCreateSchema>;

/* ────────────────────────────────────────────── UserUpdate (PATCH) */

/**
 * Phase 10F PATCH /users/{id}: partial update. Tutti i campi opzionali.
 * Lato BE: `status` richiede ruolo admin+. `role`/`email`/`owner_id` NON
 * sono modificabili via PATCH (vedi POST /users/{id}/role per il role
 * change approval-gated). Mirror del Pydantic UserUpdateRequest.
 */
export const UserUpdateSchema = z
  .object({
    display_name: z
      .string()
      .min(1, "validation.display_name_min")
      .max(128, "validation.display_name_max")
      .optional(),
    preferred_language: z.enum(["it", "en"]).optional(),
    company_name: z.string().max(128).nullable().optional(),
    notes: z.string().nullable().optional(),
    status: z.enum(STATUSES).optional(),
    branding_delegated: z.boolean().optional(),
    // Admin/superuser-only on the BE: assign / clear the user's brand.
    // `null` clears the assignment (user falls back to inheriting from
    // the nearest delegated ancestor); a UUID pins a specific brand
    // regardless of whether the user is delegated.
    brand_id: z.guid().nullable().optional(),
  })
  .strict();

export type UserUpdateInput = z.infer<typeof UserUpdateSchema>;

/* ────────────────────────────────────────────── UserRead (GET) */

export const UserReadSchema = z
  .object({
    id: z.guid(),
    email: z.string(),
    display_name: z.string(),
    role: z.enum(ROLES),
    status: z.enum(STATUSES),
    owner_id: z.guid().nullable(),
    brand_id: z.guid().nullable(),
    preferred_language: z.string(),
    mfa_enrolled: z.boolean(),
    branding_delegated: z.boolean().optional(),
    // Track B (ADR-0097/0102): true when the user administers >=1 account. Set
    // only by GET /users/me; drives account-aware nav visibility.
    is_account_admin: z.boolean().optional(),
    is_external: z.boolean().optional(),
    company_name: z.string().nullable().optional(),
    notes: z.string().nullable().optional(),
    last_login_at: z.string().nullable().optional(),
    created_at: z.string(),
    updated_at: z.string(),
  })
  .passthrough();

export type UserRead = z.infer<typeof UserReadSchema>;

/* ────────────────────────────────────────────── MFA status (admin view) */

/**
 * GET /users/{id}/mfa-status — admin view del MFA enrollment di un utente.
 * Mirror del Pydantic UserMfaStatusResponse.
 */
export const UserMfaStatusSchema = z
  .object({
    user_id: z.guid(),
    mfa_enrolled: z.boolean(),
    totp_enrolled_at: z.string().nullable(),
    totp_last_used_at: z.string().nullable(),
    recovery_codes_remaining: z.number().int().min(0),
  })
  .passthrough();

export type UserMfaStatus = z.infer<typeof UserMfaStatusSchema>;

/* ────────────────────────────────── Organizations the user is a member of */

export const UserOrgMembershipSchema = z
  .object({
    account_id: z.guid(),
    account_name: z.string(),
    account_type: z.enum(["reseller", "customer"]),
    department_id: z.guid(),
    department_name: z.string(),
    is_general: z.boolean(),
    role: z.enum(["admin", "member", "viewer"]),
    status: z.enum(["invited", "active", "revoked"]),
  })
  .passthrough();
export type UserOrgMembership = z.infer<typeof UserOrgMembershipSchema>;

export const UserOrgMembershipsResponseSchema = z
  .object({ items: z.array(UserOrgMembershipSchema) })
  .passthrough();
export type UserOrgMembershipsResponse = z.infer<typeof UserOrgMembershipsResponseSchema>;

/* ────────────────────────────────────────────── User tree (admin) */

/**
 * GET /users/tree — full owner_id forest (admin/superuser only). Mirror del
 * Pydantic UserTreeResponse / UserSubtreeNode. Il client costruisce l'albero
 * da `owner_id` (vedi lib/users-tree.ts).
 */
export const UserTreeNodeSchema = z
  .object({
    id: z.guid(),
    email: z.string(),
    display_name: z.string(),
    role: z.enum(ROLES),
    status: z.enum(STATUSES),
    owner_id: z.guid().nullable(),
    depth: z.number().int().min(0).optional(),
  })
  .passthrough();

export type UserTreeNode = z.infer<typeof UserTreeNodeSchema>;

export const UserTreeResponseSchema = z
  .object({
    nodes: z.array(UserTreeNodeSchema),
    total: z.number().int().min(0),
  })
  .passthrough();

export type UserTreeResponse = z.infer<typeof UserTreeResponseSchema>;

/* ────────────────────────────────────────────── List filters */

export const UsersListFiltersSchema = z
  .object({
    search: z.string().optional(),
    role: z.enum(ROLES).optional(),
    status: z.enum(STATUSES).optional(),
    owner_scope: z.enum(["me", "all"]).default("all"),
    // sort: "field" (asc) | "-field" (desc); field ∈ email/role/status/created_at
    sort: z.string().optional(),
    cursor: z.string().optional(),
    limit: z.number().int().min(1).max(100).default(25),
  })
  .passthrough();

// Columns the users list can sort by (header click). Mirrors BE SORT_COLUMNS.
export const USER_SORT_FIELDS = ["email", "role", "status", "created_at"] as const;
export type UserSortField = (typeof USER_SORT_FIELDS)[number];

export type UsersListFilters = z.infer<typeof UsersListFiltersSchema>;

/* ────────────────────────────────────────────── Quota pool (ADR-0096) */

export const QUOTA_POOL_DIMS = ["dns_zones", "mailbox_count", "domains", "aliases"] as const;
export type QuotaPoolDim = (typeof QUOTA_POOL_DIMS)[number];

export const QuotaPoolDimensionSchema = z
  .object({
    pool: z.number().int().nullable(),
    allocated: z.number().int().nullable(),
    available: z.number().int().nullable(),
  })
  .passthrough();

export const QuotaPoolResponseSchema = z
  .object({
    user_id: z.guid(),
    dimensions: z.record(z.string(), QuotaPoolDimensionSchema),
  })
  .passthrough();

export type QuotaPoolResponse = z.infer<typeof QuotaPoolResponseSchema>;
